/**
 * Custom event utilities
 */

/**
 * Create email signup event
 */
export function createEmailSignupEvent(email: string): CustomEvent {
  return new CustomEvent("email-signup", {
    detail: { email },
    bubbles: true,
    composed: true,
  });
}

export interface DonateClickDetail {
  /** Where the donate click originated. */
  source: "footer-callout";
  /** Which callout CTA was clicked. */
  intent: "monthly" | "once";
}

/**
 * Create donate click event.
 *
 * Cancelable — hosts call `preventDefault()` to take over the donate action
 * (e.g. open a checkout overlay) instead of the element's default navigation.
 * Mirrors the header's `donate-click` event so hosts can use one listener for
 * both components; `detail` is only present for footer-originated clicks.
 */
export function createDonateClickEvent(detail?: DonateClickDetail): CustomEvent {
  return new CustomEvent("donate-click", {
    detail,
    bubbles: true,
    composed: true,
    cancelable: true,
  });
}

/**
 * Create navigation event
 */
export function createNavigationEvent(
  url: string,
  target?: string
): CustomEvent {
  return new CustomEvent("navigate", {
    detail: { url, target },
    bubbles: true,
    composed: true,
  });
}

/**
 * Create analytics tracking event
 */
export function createTrackingEvent(
  action: string,
  category: string,
  label?: string,
  value?: number
): CustomEvent {
  return new CustomEvent("track", {
    detail: { action, category, label, value },
    bubbles: true,
    composed: true,
  });
}

/**
 * Create error event
 */
export function createErrorEvent(error: Error, context?: string): CustomEvent {
  return new CustomEvent("error", {
    detail: { error: error.message, stack: error.stack, context },
    bubbles: true,
    composed: true,
  });
}

/**
 * Dispatch custom event on element
 */
export function dispatchCustomEvent(
  element: HTMLElement,
  event: CustomEvent
): boolean {
  return element.dispatchEvent(event);
}
